import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { getMainAxios } from '../utils/util';
import { type IssuedPwd, fmtT, pwdStatus, deliveryText, copy, tempPwdConfirm } from './pwdUtil';
import {
  page, card, cardGrid, titleRow, gridTitle, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, colors, agGridBox,
} from '../styles/fdsTheme';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 * 계정관리 — 사용자 목록 · 역할 · 계좌 권한 (2026-10-27).
 *
 *   GET  /api/v1/auth/users                 사용자 목록
 *   POST /api/v1/auth/users/create          사용자 추가 (신규 가입이 닫혀 있어 관리자가 만든다)
 *   POST /api/v1/auth/users/{id}/role       역할 변경
 *   GET|POST|DELETE /api/v1/auth/pf-grants  계좌·슬롯 권한 (UserPfEndpoints)
 *   GET|POST /api/v1/auth/api-tokens[/{id}/revoke]  외부 API 토큰 — 만료 없음, 회수형
 *   POST /api/v1/auth/users/{id}/temp-password  임시 비밀번호 발급 → 원문 1회 (2026-11-01, 서버 PwdPolicy)
 *        24시간 유효 · 첫 로그인 때 변경 강제(바꾸기 전엔 서버가 다른 API 를 막는다) · 잠금도 푼다
 *
 * 규칙(서버 PfAccess): Dev = 전 계좌 · Viewer = 모의 조회만 ·
 * 그 외 = 자기 계좌 + 여기서 허락한 계좌. 주문은 '주문' 표시한 모의 계좌만, 실전 주문은 Dev 만.
 * 설계: docs/design/20261027-계좌권한-주문제한.md
 */

interface FdsUserInfo {
  id: number;
  email: string;
  name: string;
  role: number;
  roleNm: string;
  joinT: string;
  lastLogIn: string;
  accessFailedCount: number;
  emailConfirmed: boolean;
  // 비밀번호 상태 (UTC ISO)
  pwdChangedAt?: string | null;
  pwdTempAt?: string | null;
  pwdTempExp?: string | null;
  pwdMustChange?: boolean;
  pwdExpired?: boolean;
  lockedUntil?: string | null;
}

/** 방금 발급한 임시 비밀번호 — 화면에 한 번만 보여 준다 */

interface Grant {
  id: number;
  userId: number;
  email?: string;
  pfId: number;
  pfStratId?: number | null;   // null = 계좌 전체, 값 = 그 슬롯 주문만
  slotName?: string | null;
  accountNo?: string;
  htsId?: string;
  broker?: string;
  isReal: boolean;
  isActive: boolean;
  canOrder: boolean;
  memo?: string | null;
  crtAt: string;
  crtByEmail?: string | null;
}

interface ApiToken {
  id: number; userId: number; name: string; prefix: string; memo?: string | null;
  crtAt: string; crtByEmail?: string | null; lastUsedAt?: string | null; lastIp?: string | null;
  revokedAt?: string | null; active: boolean;
}

interface Slot { id: number; name: string; stratName?: string; code?: string; isSystem: boolean; enabled: boolean; }

interface Acct { id: number; idNm?: string | null; accountNo: string; htsId?: string; broker?: string; isReal: boolean; isActive?: boolean; userId: number; }

const ROLES: [number, string][] = [[0, 'None'], [5, 'Viewer'], [15, 'API주문'], [10, 'User'], [20, 'Intern'], [50, 'Manager']];
const roleLabel = (role: number) => role === 90 ? 'Dev' : (ROLES.find(r => r[0] === role)?.[1] ?? `${role}`);

const inputStyle: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px' };
const btnSm = (bg: string): React.CSSProperties => ({ padding: '2px 8px', backgroundColor: bg, color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' });
const errOf = (e: any, d: string) => e?.response?.data?.error || e?.response?.data?.message || d;

const UserManagement: React.FC = () => {
  const userCopy = useGridCopyMenu();
  const grantCopy = useGridCopyMenu();
  const [users, setUsers] = useState<FdsUserInfo[]>([]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [accts, setAccts] = useState<Acct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [nu, setNu] = useState({ email: '', name: '', password: '', role: 10 });
  const [ng, setNg] = useState({ pfId: '', slotId: '', canOrder: false, memo: '' });
  const [ngSlots, setNgSlots] = useState<Slot[]>([]);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [nt, setNt] = useState({ name: '', memo: '' });
  const [issued, setIssued] = useState<{ token: string; name: string } | null>(null);
  const [pwdIssued, setPwdIssued] = useState<IssuedPwd | null>(null);
  const [forceChange, setForceChange] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ax = getMainAxios();
      const [u, g, a, t] = await Promise.all([
        ax.get('/api/v1/auth/users'),
        ax.get('/api/v1/auth/pf-grants'),
        ax.get('/api/v1/portfolio-management/list'),
        ax.get('/api/v1/auth/api-tokens'),
      ]);
      setUsers(u.data as FdsUserInfo[]);
      setGrants(g.data as Grant[]);
      setAccts(a.data as Acct[]);
      setTokens(t.data as ApiToken[]);
    } catch (err: any) {
      setError(errOf(err, '사용자 목록 조회 실패'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 다른 사용자를 고르면 방금 발급한 토큰 원문은 지운다 (한 번만 보여 준다)
  useEffect(() => { setIssued(null); setPwdIssued(p => (p && p.userId === selId ? p : null)); }, [selId]);

  // 권한 추가 — 계좌를 고르면 그 계좌 슬롯(시스템 제외)을 불러온다
  useEffect(() => {
    if (!ng.pfId) { setNgSlots([]); return; }
    getMainAxios().get('/api/v1/pfstrat/slots', { params: { pfId: Number(ng.pfId) } })
      .then(r => setNgSlots(((r.data.slots ?? []) as Slot[]).filter(x => !x.isSystem)))
      .catch(() => setNgSlots([]));
  }, [ng.pfId]);

  const sel = users.find(u => u.id === selId) ?? null;
  const selGrants = useMemo(() => grants.filter(g => g.userId === selId), [grants, selId]);
  const ownAccts = useMemo(() => accts.filter(a => a.userId === selId), [accts, selId]);
  const roleFixed = sel != null && (sel.role >= 90 || sel.role === 5 || sel.role === 0);

  const changeRole = async (u: FdsUserInfo, role: number) => {
    if (role === u.role) return;
    if (!confirm(`${u.email} 역할을 ${roleLabel(u.role)} → ${roleLabel(role)} 로 바꿀까요?`)) return;
    try {
      await getMainAxios().post(`/api/v1/auth/users/${u.id}/role`, { role });
      fetchAll();
    } catch (err: any) { alert(errOf(err, '역할 변경 실패')); }
  };

  const createUser = async () => {
    if (!nu.email.trim() || !nu.name.trim() || nu.password.length < 6) {
      alert('이메일·이름은 필수, 비밀번호는 6자 이상입니다.');
      return;
    }
    try {
      const r = await getMainAxios().post('/api/v1/auth/users/create', nu);
      const force = !!r.data.forceChange;
      setPwdIssued({
        userId: r.data.id, email: nu.email.trim().toLowerCase(), name: nu.name.trim(), password: nu.password, forceChange: force,
        expiresAt: force ? new Date(Date.now() + 24 * 3600 * 1000).toISOString() : null,
      });
      setShowNew(false);
      setNu({ email: '', name: '', password: '', role: 10 });
      await fetchAll();
      setSelId(r.data.id);
    } catch (err: any) { alert(errOf(err, '사용자 추가 실패')); }
  };

  const issueTempPwd = async (u: FdsUserInfo) => {
    const viewer = u.role === 5;
    const force = !viewer && forceChange;
    if (!confirm(tempPwdConfirm(u.email, force, viewer))) return;
    try {
      const r = await getMainAxios().post(`/api/v1/auth/users/${u.id}/temp-password`, { forceChange: force });
      setPwdIssued({ userId: u.id, email: r.data.email, name: r.data.name, password: r.data.tempPassword, forceChange: r.data.forceChange, expiresAt: r.data.expiresAt });
      fetchAll();
    } catch (err: any) { alert(errOf(err, '임시 비밀번호 발급 실패')); }
  };

  const saveGrant = async (pfId: number, canOrder: boolean, memo?: string | null, pfStratId?: number | null) => {
    if (selId == null) return;
    try {
      await getMainAxios().post('/api/v1/auth/pf-grants', { userId: selId, pfId, canOrder, memo: memo ?? null, pfStratId: pfStratId ?? null });
      fetchAll();
    } catch (err: any) { alert(errOf(err, '권한 저장 실패')); }
  };

  const addGrant = async () => {
    if (!ng.pfId) { alert('계좌를 고르세요.'); return; }
    await saveGrant(Number(ng.pfId), ng.canOrder, ng.memo.trim() || null, ng.slotId ? Number(ng.slotId) : null);
    setNg({ pfId: '', slotId: '', canOrder: false, memo: '' });
  };

  const issueToken = async () => {
    if (selId == null) return;
    if (!nt.name.trim()) { alert('토큰 이름(용도)을 입력하세요.'); return; }
    try {
      const r = await getMainAxios().post('/api/v1/auth/api-tokens', { userId: selId, name: nt.name.trim(), memo: nt.memo.trim() || null });
      setIssued({ token: r.data.token, name: nt.name.trim() });
      setNt({ name: '', memo: '' });
      fetchAll();
    } catch (err: any) { alert(errOf(err, '토큰 발급 실패')); }
  };

  const revokeToken = async (t: ApiToken) => {
    if (!confirm(`토큰 "${t.name}" (${t.prefix}…) 을 회수할까요? 이 토큰을 쓰는 프로그램은 바로 막힙니다.`)) return;
    try {
      await getMainAxios().post(`/api/v1/auth/api-tokens/${t.id}/revoke`);
      fetchAll();
    } catch (err: any) { alert(errOf(err, '회수 실패')); }
  };

  const revoke = async (g: Grant) => {
    if (!confirm(`${g.email} 의 계좌 ${g.pfId}${g.pfStratId ? ` 슬롯 ${g.slotName ?? g.pfStratId}` : ''} 권한을 회수할까요?`)) return;
    try {
      await getMainAxios().delete(`/api/v1/auth/pf-grants/${g.id}`);
      fetchAll();
    } catch (err: any) { alert(errOf(err, '회수 실패')); }
  };

  const userCols = useMemo<ColDef<FdsUserInfo>[]>(() => [
    { field: 'id', headerName: 'ID', width: 55, cellStyle: { textAlign: 'right' } },
    { field: 'email', headerName: '이메일', width: 200 },
    { field: 'name', headerName: '이름', width: 100 },
    {
      field: 'role', headerName: '권한', width: 110, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        const u = p.data as FdsUserInfo;
        if (u.role >= 90)
          return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: '#dbeafe', color: '#1e40af' }}>Dev</span>;
        return (
          <select value={u.role} onClick={e => e.stopPropagation()} onChange={e => changeRole(u, Number(e.target.value))}
            style={{ ...selectSm, padding: '1px 4px', fontSize: '11px' }}>
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        );
      },
    },
    {
      headerName: '계좌 권한', width: 150,
      valueGetter: (p) => {
        const u = p.data!;
        if (u.role >= 90) return '전 계좌';
        if (u.role === 5) return '모의 조회';
        if (u.role === 0) return '접속 불가';
        const gs = grants.filter(g => g.userId === u.id);
        return gs.length ? gs.map(g => `${g.pfId}${g.canOrder ? '(주문)' : ''}`).join(', ') : '없음';
      },
    },
    { field: 'joinT', headerName: '가입일', width: 140, cellStyle: { textAlign: 'center' } },
    { field: 'lastLogIn', headerName: '최근로그인', width: 140, cellStyle: { textAlign: 'center' } },
    {
      headerName: '비밀번호', width: 150, cellStyle: { textAlign: 'center' },
      valueGetter: p => pwdStatus(p.data!)[0],
      cellRenderer: (p: any) => {
        const [t, c] = pwdStatus(p.data as FdsUserInfo);
        return <span style={{ color: c, fontWeight: t === '정상' ? 400 : 600 }}>{t}</span>;
      },
    },
    { headerName: '비번변경', width: 140, cellStyle: { textAlign: 'center' },
      valueGetter: p => fmtT(p.data?.pwdChangedAt) || '-' },
    {
      field: 'accessFailedCount', headerName: '로그인실패', width: 100, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => p.value > 0
        ? <span style={{ color: colors.up, fontWeight: 600 }}>{p.value}</span>
        : <span style={{ color: colors.hint }}>0</span>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [grants]);

  const grantCols = useMemo<ColDef<Grant>[]>(() => [
    { field: 'pfId', headerName: '계좌', width: 70, cellStyle: { textAlign: 'right', fontWeight: 600 } },
    { headerName: '구분', width: 70, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => p.data.isReal
        ? <span style={{ color: colors.up, fontWeight: 600 }}>실전</span>
        : <span style={{ color: '#16a34a', fontWeight: 600 }}>모의</span> },
    { field: 'broker', headerName: '브로커', width: 80, cellStyle: { textAlign: 'center' } },
    { field: 'accountNo', headerName: '계좌번호', width: 120 },
    { headerName: '범위', width: 150,
      valueGetter: p => p.data?.pfStratId ? `슬롯 ${p.data.slotName ?? p.data.pfStratId}` : '계좌 전체',
      cellStyle: (p: any) => ({ color: p.data?.pfStratId ? '#6d28d9' : colors.body, fontWeight: p.data?.pfStratId ? 600 : 400 }) },
    { headerName: '조회', width: 60, cellStyle: { textAlign: 'center' },
      cellRenderer: () => <span style={{ color: '#16a34a', fontWeight: 600 }}>●</span> },
    {
      field: 'canOrder', headerName: '주문', width: 70, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        const g = p.data as Grant;
        if (g.isReal) return <span title="실전 계좌 주문은 Dev 만" style={{ color: '#d1d5db' }}>—</span>;
        return (
          <span title="클릭: 주문 허용/해제" style={{ cursor: 'pointer' }} onClick={() => saveGrant(g.pfId, !g.canOrder, g.memo, g.pfStratId)}>
            {g.canOrder ? <span style={{ color: '#16a34a', fontWeight: 600 }}>●</span> : <span style={{ color: colors.hint }}>○</span>}
          </span>
        );
      },
    },
    { field: 'memo', headerName: '메모', flex: 1, minWidth: 140, valueFormatter: p => p.value ?? '' },
    { field: 'crtAt', headerName: '허락일', width: 110, cellStyle: { textAlign: 'center' },
      valueFormatter: p => (p.value ? String(p.value).slice(0, 10) : '') },
    { field: 'crtByEmail', headerName: '허락자', width: 150, valueFormatter: p => p.value ?? '' },
    { headerName: '작업', width: 80, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => <button style={btnSm('#ef4444')} onClick={() => revoke(p.data)}>회수</button> },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selId]);

  const grantable = accts.filter(a => a.userId !== selId);
  const selTokens = tokens.filter(t => t.userId === selId);
  const ngAcct = accts.find(a => a.id === Number(ng.pfId));

  return (
    <div style={page}>
      {/* 조회 조건 */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={toolbarLabel}>
          계정관리 — 사용자 {users.length}명 · 계좌 권한 {grants.length}건
          {(() => {
            const t = users.filter(u => u.pwdMustChange && !u.pwdExpired).length;
            const x = users.filter(u => u.pwdExpired).length;
            const l = users.filter(u => u.lockedUntil && new Date(u.lockedUntil) > new Date()).length;
            return (t || x || l) ? <span style={{ ...gridHint, marginLeft: 8 }}>· 임시 {t} · 임시만료 {x} · 잠김 {l}</span> : null;
          })()}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...btnPrimary, backgroundColor: '#16a34a' }} onClick={() => setShowNew(v => !v)}>+ 사용자 추가</button>
          <button style={btnPrimary} onClick={fetchAll}>새로고침</button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '12px' }}>
          {error}
        </div>
      )}

      {showNew && (
        <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={toolbarLabel}>새 사용자</span>
          <input style={inputStyle} placeholder="이메일" value={nu.email} onChange={e => setNu({ ...nu, email: e.target.value })} />
          <input style={inputStyle} placeholder="이름" value={nu.name} onChange={e => setNu({ ...nu, name: e.target.value })} />
          <input style={inputStyle} type="text" placeholder="첫 비밀번호 (6자 이상)" value={nu.password}
            onChange={e => setNu({ ...nu, password: e.target.value })} />
          <button style={{ ...btnPrimary, backgroundColor: '#6b7280' }} title="임의 10자 생성"
            onClick={() => {
              const cs = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
              const a = new Uint32Array(10); crypto.getRandomValues(a);
              setNu({ ...nu, password: Array.from(a, v => cs[v % cs.length]).join('') });
            }}>생성</button>
          <select style={selectSm} value={nu.role} onChange={e => setNu({ ...nu, role: Number(e.target.value) })}>
            {ROLES.filter(r => r[0] > 0).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button style={btnPrimary} onClick={createUser}>추가</button>
          <span style={gridHint}>첫 로그인 때 비밀번호를 바꾸게 한다(Viewer 제외) · 추가한 뒤 아래에서 계좌 권한을 줘야 계좌가 보인다</span>
        </div>
      )}

      {/* 사용자 목록 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>사용자 목록 <span style={gridHint}>(행 클릭: 계좌 권한 보기 · 권한 칸: 역할 변경)</span></h3>
        </div>
        <div className="ag-theme-quartz" style={agGridBox(Math.min(80 + users.length * 32, 360))}>
          <AgGridReact<FdsUserInfo> {...userCopy.gridProps}
            rowData={users}
            columnDefs={userCols}
            defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
            getRowId={p => String(p.data.id)}
            onRowClicked={e => e.data && setSelId(e.data.id)}
            getRowStyle={p => p.data?.id === selId ? { backgroundColor: '#dbeafe', fontWeight: 600 } : undefined}
            loading={loading}
          />
        </div>
        {userCopy.menu}
      </div>

      {/* 비밀번호 — 임시 비밀번호 발급·전달 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            비밀번호 {sel ? `— ${sel.email}` : ''}
            <span style={gridHint}>(임시 비밀번호는 24시간 유효 · 첫 로그인 때 변경 강제 · 발급하면 잠금도 풀린다)</span>
          </h3>
        </div>
        {!sel && <p style={emptyMsg}>위에서 사용자를 고르세요.</p>}
        {sel && sel.role >= 90 && <p style={emptyMsg}>Dev 계정은 임시 비밀번호를 발급하지 않는다 — 본인이 '비밀번호 변경'으로 바꾼다.</p>}
        {sel && sel.role < 90 && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '12px', color: colors.body, marginBottom: 10 }}>
              <span>상태: <b style={{ color: pwdStatus(sel)[1] }}>{pwdStatus(sel)[0]}</b></span>
              <span>최근 변경: {fmtT(sel.pwdChangedAt) || '기록 없음'}</span>
              {sel.pwdTempAt && <span>임시 발급: {fmtT(sel.pwdTempAt)}</span>}
              <span>최근 로그인: {sel.lastLogIn}</span>
              <span>로그인 실패: {sel.accessFailedCount}</span>
            </div>
            {pwdIssued && pwdIssued.userId === sel.id && (
              <div style={{ marginBottom: 10, padding: '10px 12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, fontSize: '12px' }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                  임시 비밀번호 발급됨 — <u>지금 한 번만</u> 보인다. 안내문을 복사해 {pwdIssued.name}님께 전달하세요.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  <code style={{ padding: '4px 10px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 3, fontSize: '14px', letterSpacing: 1 }}>{pwdIssued.password}</code>
                  <button style={btnPrimary} onClick={() => copy(pwdIssued.password, '비밀번호를')}>비밀번호 복사</button>
                  <button style={{ ...btnPrimary, backgroundColor: '#16a34a' }} onClick={() => copy(deliveryText(pwdIssued), '안내문을')}>안내문 복사</button>
                  <button style={{ ...btnPrimary, backgroundColor: '#6b7280' }} onClick={() => setPwdIssued(null)}>닫기</button>
                </div>
                <pre style={{ margin: 0, padding: '6px 10px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 3, fontSize: '12px', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {deliveryText(pwdIssued)}
                </pre>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px', color: sel.role === 5 ? colors.hint : colors.body }}
                title={sel.role === 5 ? 'Viewer 는 공용 계정이라 본인이 비밀번호를 바꿀 수 없다' : ''}>
                <input type="checkbox" checked={sel.role !== 5 && forceChange} disabled={sel.role === 5}
                  onChange={e => setForceChange(e.target.checked)} />
                첫 로그인 때 변경 강제
              </label>
              <button style={{ ...btnPrimary, backgroundColor: '#d97706' }} onClick={() => issueTempPwd(sel)}>임시 비밀번호 발급</button>
            </div>
          </>
        )}
      </div>

      {/* 계좌 권한 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            계좌 권한 {sel ? `— ${sel.email} (${roleLabel(sel.role)})` : ''}
            <span style={gridHint}>(주문 칸 클릭: 허용/해제 · 실전 계좌는 조회만 줄 수 있다)</span>
          </h3>
        </div>

        {!sel && <p style={emptyMsg}>위에서 사용자를 고르세요.</p>}
        {sel && roleFixed && (
          <p style={emptyMsg}>
            {sel.role >= 90 ? 'Dev 는 전 계좌를 조회·주문한다' : sel.role === 5 ? 'Viewer 는 모의 계좌 조회만 한다' : 'None 은 접속할 수 없다'}
            {' '}— 역할로 정해지며 계좌 권한표를 보지 않는다.
          </p>
        )}
        {sel && !roleFixed && (
          <>
            {ownAccts.length > 0 && (
              <p style={{ ...emptyMsg, color: colors.body }}>
                본인 소유 계좌(자동 허용): {ownAccts.map(a => `${a.id}${a.isReal ? '(실전·조회만)' : ''}`).join(', ')}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: '12px' }}>
              <select style={selectSm} value={ng.pfId}
                onChange={e => {
                  const a = accts.find(x => x.id === Number(e.target.value));
                  setNg({ ...ng, pfId: e.target.value, canOrder: a?.isReal ? false : ng.canOrder });
                }}>
                <option value="">계좌 선택</option>
                {grantable.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.id} {a.idNm ?? ''} · {a.isReal ? '실전' : '모의'} · {a.broker ?? 'kis'} {a.accountNo}{a.isActive === false ? ' (정지)' : ''}
                  </option>
                ))}
              </select>
              <select style={selectSm} value={ng.slotId} disabled={!ng.pfId} onChange={e => setNg({ ...ng, slotId: e.target.value })}
                title='슬롯을 고르면 그 슬롯 주문만 허용한다 (조회는 계좌 전체)'>
                <option value=''>계좌 전체</option>
                {ngSlots.map(x => <option key={x.id} value={x.id}>슬롯 {x.id} · {x.name}{x.enabled ? '' : ' (꺼짐)'}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: ngAcct?.isReal ? colors.hint : colors.body }}
                title={ngAcct?.isReal ? '실전 계좌 주문은 Dev 만' : ''}>
                <input type="checkbox" checked={ng.canOrder} disabled={!!ngAcct?.isReal}
                  onChange={e => setNg({ ...ng, canOrder: e.target.checked })} />
                주문 허용
              </label>
              <input style={{ ...inputStyle, width: 200 }} placeholder="메모 (예: 외부 테스트)" value={ng.memo}
                onChange={e => setNg({ ...ng, memo: e.target.value })} />
              <button style={btnPrimary} onClick={addGrant}>+ 권한 추가</button>
            </div>
            {selGrants.length === 0
              ? <p style={emptyMsg}>허락된 계좌가 없습니다 — 본인 소유 계좌 외에는 아무것도 보이지 않는다.</p>
              : (
                <div className="ag-theme-quartz" style={agGridBox(Math.min(80 + selGrants.length * 32, 300))}>
                  <AgGridReact<Grant> {...grantCopy.gridProps}
                    rowData={selGrants}
                    columnDefs={grantCols}
                    defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
                    getRowId={p => String(p.data.id)}
                  />
                </div>
              )}
            {grantCopy.menu}
          </>
        )}
      </div>

      {/* API 토큰 — 만료 없음, 회수형. 외부 프로그램이 Authorization: Bearer fdsk_... 로 쓴다 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            API 토큰 {sel ? `— ${sel.email}` : ''}
            <span style={gridHint}>(만료 없음 · 회수할 때까지 유효 · 권한은 그 사용자의 역할·계좌 권한을 따른다)</span>
          </h3>
        </div>
        {!sel && <p style={emptyMsg}>위에서 사용자를 고르세요.</p>}
        {sel && sel.role >= 90 && <p style={emptyMsg}>Dev 계정에는 API 토큰을 발급하지 않는다 — 새면 전 계좌(실전 포함)가 열린다.</p>}
        {sel && sel.role < 90 && (
          <>
            {issued && (
              <div style={{ marginBottom: 10, padding: '10px 12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, fontSize: '12px' }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                  토큰 "{issued.name}" 발급됨 — <u>지금 한 번만</u> 보인다. 복사해서 사용자에게 전달하세요.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{ padding: '4px 8px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 3, wordBreak: 'break-all' }}>{issued.token}</code>
                  <button style={btnPrimary} onClick={() => { navigator.clipboard?.writeText(issued.token); alert('복사했습니다.'); }}>복사</button>
                  <button style={{ ...btnPrimary, backgroundColor: '#6b7280' }} onClick={() => setIssued(null)}>닫기</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <input style={{ ...inputStyle, width: 180 }} placeholder="토큰 이름 (예: 주문봇)" value={nt.name} onChange={e => setNt({ ...nt, name: e.target.value })} />
              <input style={{ ...inputStyle, width: 220 }} placeholder="메모" value={nt.memo} onChange={e => setNt({ ...nt, memo: e.target.value })} />
              <button style={btnPrimary} onClick={issueToken}>+ 토큰 발급</button>
            </div>
            {selTokens.length === 0
              ? <p style={emptyMsg}>발급된 토큰이 없습니다.</p>
              : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                    <thead><tr>
                      {['ID', '이름', '토큰(앞)', '상태', '발급', '발급자', '마지막 사용', 'IP', '메모', ''].map(h =>
                        <th key={h} style={{"textAlign":"left","padding":"4px 8px","borderBottom":"1px solid #e2e8f0","backgroundColor":"#f8fafc","fontWeight":600}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {selTokens.map(t => (
                        <tr key={t.id} style={{ color: t.active ? colors.body : colors.hint }}>
                          <td style={{ padding: '4px 8px' }}>{t.id}</td>
                          <td style={{ padding: '4px 8px', fontWeight: 600 }}>{t.name}</td>
                          <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{t.prefix}…</td>
                          <td style={{ padding: '4px 8px' }}>{t.active
                            ? <span style={{ color: '#16a34a', fontWeight: 600 }}>사용중</span>
                            : <span>회수 {String(t.revokedAt).slice(0, 16).replace('T', ' ')}</span>}</td>
                          <td style={{ padding: '4px 8px' }}>{String(t.crtAt).slice(0, 16).replace('T', ' ')}</td>
                          <td style={{ padding: '4px 8px' }}>{t.crtByEmail ?? ''}</td>
                          <td style={{ padding: '4px 8px' }}>{t.lastUsedAt ? String(t.lastUsedAt).slice(0, 16).replace('T', ' ') : '-'}</td>
                          <td style={{ padding: '4px 8px' }}>{t.lastIp ?? ''}</td>
                          <td style={{ padding: '4px 8px' }}>{t.memo ?? ''}</td>
                          <td style={{ padding: '4px 8px' }}>{t.active && <button style={btnSm('#ef4444')} onClick={() => revokeToken(t)}>회수</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
